import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Clock, Zap, Calendar, TrendingUp, AlertCircle, Package } from 'lucide-react';
import type { Agency } from '@/types';
import { formatDate } from '@/lib/utils';

interface CapacityTrackerProps {
  agency: Agency;
}

export function CapacityTracker({ agency }: CapacityTrackerProps) {
  const usagePercentage = Math.round((agency.hoursUsed / agency.monthlyHours) * 100);

  const packageConfig = {
    starter: { name: 'Starter', color: 'bg-slate-500', icon: '🚀', hours: 40 },
    growth: { name: 'Growth', color: 'bg-blue-500', icon: '📈', hours: 80 },
    sprint: { name: 'Sprint', color: 'bg-purple-500', icon: '⚡', hours: 160 },
  };

  const pkg = packageConfig[agency.packageType];

  const getStatusColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  return (
    <Card
      title="Capacity Tracker"
      subtitle="Monthly retainer usage and package details"
      action={
        <Button variant="ghost" size="sm" leftIcon={<TrendingUp className="w-4 h-4" />}>
          View History
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Package badge */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-white border-2 border-slate-200 flex items-center justify-center text-2xl shadow-sm">
              {pkg.icon}
            </div>
            <div>
              <div className="text-sm text-slate-500">Current Package</div>
              <div className="font-semibold text-slate-900">{pkg.name} Plan</div>
            </div>
          </div>
          <Badge variant="info" className="text-xs">
            {agency.monthlyHours} hrs/month
          </Badge>
        </div>

        {/* Hours usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Hours Used</span>
            <span className="text-sm text-slate-500">
              {agency.hoursUsed} / {agency.monthlyHours} hrs
            </span>
          </div>
          <ProgressBar value={usagePercentage} color={getStatusColor(usagePercentage)} size="lg" />
          <div className="flex items-center justify-between mt-1">
            <span className={`text-xs ${usagePercentage >= 90 ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
              {usagePercentage}% used
            </span>
            {usagePercentage >= 90 && (
              <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Running low!
              </span>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Remaining</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{agency.hoursRemaining}</div>
            <div className="text-xs text-slate-500">hours available</div>
          </div>

          {agency.rolloverHours > 0 && (
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600 font-medium">Rollover</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">+{agency.rolloverHours}</div>
              <div className="text-xs text-slate-500">bonus hours</div>
            </div>
          )}

          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-600 font-medium">Extra Rate</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">${agency.extraHourRate}</div>
            <div className="text-xs text-slate-500">per extra hour</div>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-600 font-medium">Renews</span>
            </div>
            <div className="text-xl font-bold text-slate-900">{formatDate(agency.renewalDate)}</div>
            <div className="text-xs text-slate-500">next billing cycle</div>
          </div>
        </div>

        {/* Upgrade CTA */}
        {usagePercentage >= 80 && (
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Running low on hours?</h4>
                <p className="text-sm text-slate-600 mt-1">
                  You have {agency.hoursRemaining} hours left. Consider upgrading to the{' '}
                  {agency.packageType === 'starter' ? 'Growth' : agency.packageType === 'growth' ? 'Sprint' : 'Enterprise'} plan for more capacity.
                </p>
                <Button variant="outline" size="sm" className="mt-3">
                  Explore Plans
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
